import React, { useMemo, useState } from 'react'
import { push } from '@lagunovsky/redux-react-router'
import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import ReferencialAction from '../../action/ReferencialAction'
import DtoSandreCode from '../../dto/DtoSandreCode'
import { getUser } from 'utils/SettingUtils'
import { hasValue } from 'utils/NumberUtil'
import SandreCodePanel from '../sandreCodes/SandreCodePanel'
import { useParams } from 'react-router'
import { Box, Card, CardContent } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'

const LexiconApp = () => {
    const { field, code } = useParams()
    const {
        sandreCodes,
        referencialSandreCodes,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        referencialSandreCodes: store.ReferencialReducer.referencialSandreCodes,
    }), shallowEqual)

    const [sandreCode, setSandreCode] = useState(new DtoSandreCode({}))
    const [readMode, setReadMode] = useState(field !== 'new')

    const dispatch = useDispatch()

    const defaultSandreCode = useMemo(() => sandreCodes.find(sc => sc.field === field && sc.code === parseInt(code)) || {}, [code, field, sandreCodes])

    useEffect(() => {
        if (field !== 'new') {
            setSandreCode(defaultSandreCode)
        }
    }, [defaultSandreCode, field, sandreCodes])

    useEffect(() => {
        dispatch(ReferencialAction.fetchSandreCodes(true))

        if (!referencialSandreCodes.length) {
            dispatch(ReferencialAction.fetchReferencialSandreCodes())
        }
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: '/referencial',
    }, {
        title: i18n.lexicons,
        href: '/referencial/lexicons',
    }, {
        title: field !== 'new' ? field : i18n.new,
        href: `/referencial/lexicon/${field}/${code}/dashboard`,
    }], [field, code])

    const isConsultant = useMemo(() => getUser().consultant === '1', [])

    useActions(() => {
        if (isConsultant) {
            return {}
        }
        if (field === 'new' || !readMode) {
            return {
                save: () => {
                    if (hasValue(sandreCode.code) && sandreCode.field && sandreCode.name) {
                        if (sandreCode.mnemonique) {
                            const regex = new RegExp('^[A-Z0-9_-]*$')
                            if (!regex.test(sandreCode.mnemonique)) {
                                dispatch(ToastrAction.warning(i18n.mnemoniqueRule))
                            }
                        }
                        if (field === 'new') {
                            if (sandreCodes.find(s => s.field === sandreCode.field && s.code === sandreCode.code)) {
                                dispatch(ToastrAction.error(i18n.undefinedOrTakenCodeAndName))
                                return
                            }
                            dispatch(ReferencialAction.addSandreCode(sandreCode)).then(() => {
                                dispatch(ReferencialAction.fetchSandreCodes(true))
                                dispatch(push('/referencial/lexicons'))
                            })
                            setReadMode(true)
                        } else {
                            dispatch(ReferencialAction.updateSandreCode(sandreCode)).then(() => {
                                dispatch(ReferencialAction.fetchSandreCodes(true))
                            })
                            setReadMode(true)
                        }
                    } else {
                        dispatch(ToastrAction.error(i18n.allFieldsAreRequired))
                    }
                },
                cancel: () => {
                    if (field === 'new') {
                        dispatch(push('/referencial/lexicons'))
                    } else {
                        setSandreCode(defaultSandreCode)
                        setReadMode(true)
                    }
                },
            }
        }
        return {
            edit: () => {
                setReadMode(false)
            },
        }
    }, [field, readMode, defaultSandreCode, sandreCode])

    return (
        <Box margin='10px 10px 10px 0'>
            <Card>
                <CardTitle title={i18n.description}/>
                <CardContent>
                    <SandreCodePanel
                        sandreCode={sandreCode}
                        readMode={readMode}
                        onChange={value => setSandreCode(sandreObj => ({ ...sandreObj, ...value }))}
                        isSandre={false}
                    />
                </CardContent>
            </Card>
        </Box>
    )
}

export default LexiconApp
